import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
interface LegalModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'privacy' | 'terms' | null;
}
const content = {
  privacy: {
    title: 'Política de Privacidad',
    sections: [{
      heading: '1. Información que Recopilamos',
      body: 'Recopilamos la información que nos proporcionas directamente al realizar una compra, incluyendo tu nombre, dirección de correo electrónico e información de pago necesaria para procesar tu transacción de manera segura.'
    }, {
      heading: '2. Uso de la Información',
      body: 'Utilizamos tu información para procesar tu compra, entregarte el acceso al Plan de Recuperación de las 24 Horas, enviarte actualizaciones relacionadas con el producto y mejorar tu experiencia como usuario.'
    }, {
      heading: '3. Protección de Datos',
      body: 'Implementamos medidas de seguridad técnicas y organizativas para proteger tu información personal contra acceso no autorizado, pérdida o uso indebido. Tus datos de pago son procesados a través de pasarelas seguras y cifradas.'
    }, {
      heading: '4. Compartir Información con Terceros',
      body: 'No vendemos, alquilamos ni compartimos tu información personal con terceros para fines comerciales. Solo compartimos datos cuando es estrictamente necesario para procesar pagos o cumplir con obligaciones legales.'
    }, {
      heading: '5. Cookies y Tecnologías Similares',
      body: 'Nuestro sitio puede utilizar cookies para mejorar la funcionalidad y analizar el tráfico. Puedes configurar tu navegador para rechazar las cookies, aunque esto puede afectar algunas funciones del sitio.'
    }, {
      heading: '6. Tus Derechos',
      body: 'Tienes derecho a acceder, corregir o eliminar tu información personal en cualquier momento. También puedes solicitar la portabilidad de tus datos o presentar una queja ante la autoridad de protección de datos correspondiente.'
    }, {
      heading: '7. Cambios en esta Política',
      body: 'Nos reservamos el derecho de actualizar esta política de privacidad en cualquier momento. Los cambios entrarán en vigor al publicarse en esta página, indicando la fecha de la última actualización.'
    }]
  },
  terms: {
    title: 'Términos y Condiciones',
    sections: [{
      heading: '1. Aceptación de los Términos',
      body: 'Al acceder y utilizar el Plan de Recuperación de las 24 Horas, aceptas estar sujeto a estos términos y condiciones en su totalidad. Si no estás de acuerdo con alguna parte, no debes utilizar este producto.'
    }, {
      heading: '2. Naturaleza del Producto',
      body: 'El Plan de Recuperación de las 24 Horas es un producto educativo digital diseñado para proporcionar estrategias psicológicas y operativas para traders. No constituye asesoramiento financiero, fiscal o de inversión personalizado.'
    }, {
      heading: '3. Aviso de Riesgo',
      body: 'El trading conlleva un alto nivel de riesgo y puede no ser adecuado para todos los inversores. Existe la posibilidad de que pierdas la totalidad o más de tu inversión inicial. Los resultados pasados no garantizan ni indican resultados futuros.'
    }, {
      heading: '4. Sin Garantías de Resultados',
      body: 'No garantizamos ganancias, rendimientos específicos ni recuperación de pérdidas. El éxito en el trading depende de múltiples factores personales, incluyendo experiencia, disciplina, capital y condiciones del mercado.'
    }, {
      heading: '5. Propiedad Intelectual',
      body: 'Todo el contenido del Plan de Recuperación de las 24 Horas (texto, manuales, matrices, checklists y material asociado) está protegido por derechos de autor. Se prohíbe la reproducción, distribución o reventa sin autorización expresa por escrito.'
    }, {
      heading: '6. Licencia de Uso Personal',
      body: 'Al adquirir el producto, recibes una licencia personal, no transferible y no exclusiva para utilizar el material únicamente para tu uso individual. No puedes compartir tus credenciales de acceso ni el contenido con terceros.'
    }, {
      heading: '7. Política de Reembolsos',
      body: 'Debido a la naturaleza digital y de entrega inmediata del producto, todas las ventas son finales. Te recomendamos revisar cuidadosamente la información del producto antes de realizar tu compra.'
    }, {
      heading: '8. Limitación de Responsabilidad',
      body: 'Bajo ninguna circunstancia seremos responsables de pérdidas financieras, daños directos, indirectos, incidentales o consecuentes derivados del uso o la imposibilidad de uso de este material educativo.'
    }, {
      heading: '9. Modificaciones',
      body: 'Nos reservamos el derecho de modificar estos términos en cualquier momento. El uso continuado del producto tras dichas modificaciones constituye tu aceptación de los nuevos términos.'
    }]
  }
};
export const LegalModal: React.FC<LegalModalProps> = ({
  isOpen,
  onClose,
  type
}) => {
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleEsc);
    }
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, onClose]);
  const data = type ? content[type] : null;
  return <AnimatePresence data-id="element-83">
      {isOpen && data && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="legal-modal-title" data-id="element-84">
          <motion.div initial={{
        opacity: 0,
        y: 20,
        scale: 0.96
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.96
      }} transition={{
        duration: 0.25,
        ease: 'easeOut'
      }} className="relative w-full max-w-3xl max-h-[85vh] bg-brand-surface border border-brand-red/30 rounded-2xl shadow-[0_0_60px_rgba(239,68,68,0.15)] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-85">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-white/10 bg-black/40 shrink-0" data-id="element-86">
              <div data-id="element-87">
                <p className="font-mono text-brand-red text-xs uppercase tracking-widest mb-1" data-id="element-88">
                  Documento Legal
                </p>
                <h2 id="legal-modal-title" className="text-2xl md:text-3xl font-display text-white" data-id="element-89">
                  {data.title}
                </h2>
              </div>
              <button onClick={onClose} className="shrink-0 w-10 h-10 rounded-full bg-white/5 hover:bg-brand-red/20 border border-white/10 hover:border-brand-red/50 flex items-center justify-center transition-colors group" aria-label="Cerrar" data-id="element-90">
                <X className="w-5 h-5 text-gray-400 group-hover:text-white" data-id="element-91" />
              </button>
            </div>

            {/* Content */}
            <div className="overflow-y-auto px-6 md:px-8 py-8 space-y-6 flex-1" data-id="element-92">
              <p className="text-xs font-mono text-gray-500 uppercase tracking-wider" data-id="element-93">
                Última actualización:{' '}
                {new Date().toLocaleDateString('es-ES', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
              </p>
              {data.sections.map((section, i) => <div key={i} className="space-y-2" data-id="element-94">
                  <h3 className="text-lg font-display text-white tracking-wide" data-id="element-95">
                    {section.heading}
                  </h3>
                  <p className="text-gray-400 leading-relaxed text-sm md:text-base" data-id="element-96">
                    {section.body}
                  </p>
                </div>)}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-white/10 bg-black/40 text-center shrink-0" data-id="element-97">
              <button onClick={onClose} className="font-mono text-xs uppercase tracking-widest text-gray-400 hover:text-brand-red transition-colors" data-id="element-98">
                Cerrar Documento
              </button>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
};